import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Partner } from '@/types/partner';
import { useLanguage } from '@/context/LanguageContext';

interface PartnersCarouselProps {
  partners: Partner[];
}

const PartnersCarousel: React.FC<PartnersCarouselProps> = ({ partners }) => {
  const navigate = useNavigate();
  const { tl } = useLanguage();

  const handlePartnerClick = (partnerId: string) => {
    // Animate the logo before navigation
    const logo = document.getElementById(`partner-logo-${partnerId}`);
    if (logo) {
      logo.style.transition = 'all 0.3s ease';
      logo.style.transform = 'scale(1.1)';
    }

    // Add a small delay before navigation to allow the animation to complete
    setTimeout(() => {
      navigate(`/partners/${partnerId}`);
    }, 300);
  };

  return (
    <section className="pt-8 md:pt-20 pb-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl text-black md:text-4xl font-sans font-semibold mb-4">
            {tl('HO RE CA', 'HO RE CA', 'HO RE CA')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {tl(
              'Découvrez les projets de luxe que nous avons réalisés pour nos partenaires',
              'Discover the luxury projects we have completed for our partners',
              'Scoprite i progetti di lusso che abbiamo realizzato per i nostri partner'
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {partners.map((partner) => (
            <motion.div
              key={partner.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="group relative"
            >
              <motion.div
                className="aspect-square bg-white rounded-lg p-8 flex items-center justify-center cursor-pointer overflow-hidden"
                onClick={() => handlePartnerClick(partner.id)}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <motion.img
                  id={`partner-logo-${partner.id}`}
                  src={partner.logo}
                  alt={partner.name}
                  className="w-full h-full  object-contain transition-all duration-300"
                  initial={{ scale: 1 }}
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                />
                <motion.div
                  className="absolute inset-0 text-black bg-black/0 group-hover:bg-black/5 transition-all duration-300"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                />
              </motion.div>
              <motion.div
                className="text-center text-black mt-4"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <p className="text-lg font-medium">{partner.name}</p>
                {partner.location && (
                  <p className="text-sm text-muted-foreground mt-1">{partner.location}</p>
                )}
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PartnersCarousel;
